/**
 * AUTH — docs/03 §3.2.
 *
 * Two layers (D3). The **session** is an httpOnly cookie set by
 * `POST /auth/siwe`; the client never reads it and never stores a bearer token.
 * The **session key** is a browser-generated keypair whose private material is a
 * non-extractable `CryptoKey` in IndexedDB — only its address ever crosses the
 * network, in either direction.
 *
 * Timing that belongs to the product rather than to HTTP, restated because it
 * is easy to lose: a key is registered on the **first ticket focus, not on
 * Buy**, and re-registered at `T+20` of the current market when it would expire
 * inside 120 seconds — never at a roll boundary, where a wallet popup would eat
 * the whole 13-second gap (§3.2, plan Phase 5).
 */

import type { HttpClient, MutationOptions, RequestOptions } from '../client'
import type { OkBody, Page, OpBody, OpQuery } from '../types'

export function authEndpoints(client: HttpClient) {
  return {
    /** Single-use, 5-minute TTL, bound to the address. The response also carries
     *  the exact `domain`, `uri`, `chain_id` and `statement` to embed in the SIWE
     *  message — building those from `window.location` is how phishing-adjacent
     *  bugs ship, so the server's values are used verbatim. */
    getSiweNonce(query: OpQuery<'getSiweNonce'>, options?: RequestOptions) {
      return client.get<OkBody<'getSiweNonce'>>('/auth/nonce', { ...options, query })
    },

    /** Signing *is* acceptance: the statement line carries the terms version
     *  (register Part C). Sets `ud_session`. */
    verifySiwe(body: OpBody<'verifySiwe'>, options?: MutationOptions) {
      return client.post<OkBody<'verifySiwe'>>('/auth/siwe', body, options)
    },

    /** Once at cold load, in parallel with the public wave — it gates nothing
     *  public. Again after any `401`, and on wake after >10 minutes hidden. */
    getSession(options?: RequestOptions) {
      return client.get<OkBody<'getSession'>>('/auth/session', options)
    },

    /** Also called on a `SESSION_EXPIRED` error frame, to clear local state
     *  deterministically rather than leaving a half-authenticated tab. */
    logout(body?: OpBody<'logout'>, options?: MutationOptions) {
      return client.post<void>('/auth/logout', body ?? {}, options)
    },

    /** One wallet popup per session (D3). Every one of the sixty orders an
     *  active user places in the following hour costs zero. */
    registerSessionKey(body: OpBody<'registerSessionKey'>, options?: MutationOptions) {
      return client.post<OkBody<'registerSessionKey'>>('/auth/session-keys', body, options)
    },

    /** Cold load **only if** a local key exists in IndexedDB, to catch a key
     *  revoked from another device. Then on the Settings screen. */
    listSessionKeys(options?: RequestOptions) {
      return client.get<Page<OkBody<'listSessionKeys'>>>('/auth/session-keys', options)
    },

    revokeSessionKey(keyAddress: string, options?: MutationOptions) {
      return client.delete<void>(`/auth/session-keys/${encodeURIComponent(keyAddress)}`, options)
    },
  }
}

export type AuthEndpoints = ReturnType<typeof authEndpoints>
